import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle, useRef } from 'react';
import { getMediaList, deleteMedia, redownloadMedia, forceDeleteMedia, uploadMedia } from '../../services/mediaApi';
import {
    Image, File, FileText, Music, Film,
    Download, Trash2, Search, Filter, RefreshCw,
    Copy, Check, Upload
} from 'lucide-react';
import { debounce } from 'lodash';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../ui/tooltip';
const API_BASE_URL = window._env_ ? window._env_.API_URL : process.env.REACT_APP_API_URL;
const MediaList = forwardRef(({ onMediaSelect, selectedMedia }, ref) => {
    const [mediaFiles, setMediaFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [copiedId, setCopiedId] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 12,
        total: 0,
        pages: 0
    });
    const [filters, setFilters] = useState({
        type: '',
        status: '',
        search: ''
    });
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef(null);

    const fetchMedia = async (page = 1, params = {}) => {
        try {
            setLoading(true);
            const response = await getMediaList({
                page,
                limit: pagination.limit,
                ...filters,
                ...params
            });

            // Handle response from apiService directly
            if (response.status === 'success') {
                setMediaFiles(response.data.media);
                setPagination(response.data.pagination);
                setError(null);
            } else {
                throw new Error(response.message || 'Failed to load media');
            }
        } catch (err) {
            setError('Failed to load media files');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Expose the refresh method to parent components
    useImperativeHandle(ref, () => ({
        refreshMedia: () => fetchMedia(pagination.page)
    }));

    // Debounced search
    const debouncedSearch = useCallback(
        debounce((searchTerm) => {
            setFilters(prev => ({ ...prev, search: searchTerm }));
            fetchMedia(1, { search: searchTerm });
        }, 500),
        []
    );

    useEffect(() => {
        fetchMedia();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePageChange = (newPage) => {
        if (newPage < 1 || newPage > pagination.pages) return;
        fetchMedia(newPage);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        fetchMedia(1, { [name]: value });
    };

    const handleSearchChange = (e) => {
        debouncedSearch(e.target.value);
    };

    const handleDeleteMedia = async (id, status) => {
        const confirmMessage = status === 'deleted'
            ? 'This will permanently remove the file record from the database. Are you sure?'
            : 'Are you sure you want to delete this media file?';

        if (window.confirm(confirmMessage)) {
            try {
                let response;

                if (status === 'deleted') {
                    // If already deleted, use force delete to remove from database
                    response = await forceDeleteMedia(id);
                } else {
                    // Otherwise use regular delete (soft delete)
                    response = await deleteMedia(id);
                }

                // Refresh the list
                fetchMedia(pagination.page);
            } catch (err) {
                setError('Failed to delete media file');
                console.error(err);
            }
        }
    };

    const handleRedownloadMedia = async (id, mediaId) => {
        try {
            setLoading(true);
            await redownloadMedia(id, mediaId);
            // Refresh the list
            fetchMedia(pagination.page);
        } catch (err) {
            setError('Failed to redownload media file');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const getMediaIcon = (type) => {
        switch (type.toLowerCase()) {
            case 'image':
                return <Image className="h-10 w-10 text-blue-500" />;
            case 'video':
                return <Film className="h-10 w-10 text-purple-500" />;
            case 'audio':
                return <Music className="h-10 w-10 text-green-500" />;
            case 'document':
                return <FileText className="h-10 w-10 text-amber-500" />;
            default:
                return <File className="h-10 w-10 text-gray-500" />;
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Helper function to get complete media URL
    const getFullMediaUrl = (path) => {
        if (!path) return null;
        // If path already has http or https, return as is
        if (path.startsWith('http://') || path.startsWith('https://')) {
            return path;
        }
        // Otherwise, join with API_BASE_URL
        return `${API_BASE_URL}${path}`;
    };

    const handleCopyUrl = (e, file) => {
        e.stopPropagation(); // Prevent card selection
        if (file.storageInfo?.publicUrl) {
            const fullUrl = getFullMediaUrl(file.storageInfo.publicUrl);

            // Try to use the Clipboard API with fallback to execCommand
            const copyToClipboard = (text) => {
                // Try using the Clipboard API first
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    return navigator.clipboard.writeText(text)
                        .then(() => true)
                        .catch(() => false);
                }

                // Fallback to execCommand
                try {
                    const textarea = document.createElement('textarea');
                    textarea.value = text;
                    textarea.style.position = 'fixed';  // Prevent scrolling to bottom
                    document.body.appendChild(textarea);
                    textarea.focus();
                    textarea.select();

                    const successful = document.execCommand('copy');
                    document.body.removeChild(textarea);
                    return Promise.resolve(successful);
                } catch (err) {
                    return Promise.resolve(false);
                }
            };

            copyToClipboard(fullUrl)
                .then((success) => {
                    if (success) {
                        setCopiedId(file._id);
                        setTimeout(() => setCopiedId(null), 2000);
                    } else {
                        alert('Failed to copy URL. Please select and copy manually.');
                    }
                });
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            setUploading(true);
            setUploadProgress(0);
            setError(null);

            // Default metadata from filename
            const metadata = {
                name: file.name,
                description: `Uploaded on ${new Date().toLocaleString()}`
            };

            // Upload the file
            await uploadMedia(file, metadata);

            // Reset the file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

            // Refresh the media list
            fetchMedia(pagination.page);
        } catch (err) {
            setError(`Failed to upload media: ${err.message}`);
            console.error('Upload error:', err);
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const triggerFileUpload = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    return (
        <div className="p-6">
            <div className="flex flex-col">
                <h1 className="text-2xl font-bold text-gray-900 mb-6">Media Files</h1>

                {/* Filters and Search */}
                <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="relative flex items-center">
                            <Search className="absolute left-3 h-5 w-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search files..."
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                onChange={handleSearchChange}
                            />
                        </div>

                        <div className="flex items-center gap-2">
                            <Filter className="h-5 w-5 text-gray-500" />
                            <select
                                name="type"
                                value={filters.type}
                                onChange={handleFilterChange}
                                className="border border-gray-300 rounded-lg py-2 px-3 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">All Types</option>
                                <option value="image">Images</option>
                                <option value="video">Videos</option>
                                <option value="audio">Audio</option>
                                <option value="document">Documents</option>
                            </select>
                        </div>

                        <div className="flex items-center gap-2">
                            <select
                                name="status"
                                value={filters.status}
                                onChange={handleFilterChange}
                                className="border border-gray-300 rounded-lg py-2 px-3 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">All Status</option>
                                <option value="uploaded">Uploaded</option>
                                <option value="pending">Pending</option>
                                <option value="failed">Failed</option>
                                <option value="deleted">Deleted</option>
                            </select>
                        </div>
                    </div>

                    <div className="flex flex-shrink-0 space-x-2">
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileUpload}
                            className="hidden"
                        />
                        <button
                            onClick={triggerFileUpload}
                            disabled={uploading}
                            className="flex items-center gap-2 bg-green-50 text-green-600 py-2 px-4 rounded-lg hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {uploading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                                    Uploading...
                                </>
                            ) : (
                                <>
                                    <Upload className="h-4 w-4" />
                                    Upload
                                </>
                            )}
                        </button>

                        <button
                            onClick={() => fetchMedia(pagination.page)}
                            className="flex items-center gap-2 bg-blue-50 text-blue-600 py-2 px-4 rounded-lg hover:bg-blue-100"
                        >
                            <RefreshCw className="h-4 w-4" />
                            Refresh
                        </button>
                    </div>
                </div>

                {/* Media Grid */}
                {loading ? (
                    <div className="flex justify-center p-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    </div>
                ) : error ? (
                    <div className="bg-red-50 p-4 rounded-lg text-red-600">{error}</div>
                ) : mediaFiles.length === 0 ? (
                    <div className="bg-gray-50 p-12 rounded-lg text-center">
                        <File className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-xl font-medium text-gray-600">No media files found</h3>
                        <p className="text-gray-500 mt-2">Try changing your search or filter criteria.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {mediaFiles.map((file) => (
                            <div
                                key={file._id}
                                className={`bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden hover:shadow-md hover:border-blue-200 transition-all cursor-pointer ${selectedMedia?._id === file._id ? 'ring-2 ring-blue-500' : ''
                                    }`}
                                onClick={() => onMediaSelect(file)}
                            >
                                <div className="flex flex-col h-full">
                                    {/* Preview Area */}
                                    <div className="h-40 bg-gray-50 flex items-center justify-center p-4 border-b">
                                        {file.type === 'image' && file.storageInfo?.publicUrl ? (
                                            <img
                                                src={getFullMediaUrl(file.storageInfo.publicUrl)}
                                                alt={file.originalName}
                                                className="max-h-full object-contain"
                                            />
                                        ) : (
                                            <div className="flex flex-col items-center">
                                                {getMediaIcon(file.type)}
                                                <span className="text-sm font-medium text-gray-500 mt-2">
                                                    {file.type}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* File Info */}
                                    <div className="p-4 flex flex-col flex-1">
                                        <h3 className="font-medium text-gray-900 truncate" title={file.originalName}>
                                            {file.originalName}
                                        </h3>
                                        <div className="mt-2 space-y-1 text-sm text-gray-500 flex-1">
                                            <p>{formatFileSize(file.size)}</p>
                                            <p>{new Date(file.createdAt).toLocaleString()}</p>
                                            <p className="capitalize">Status: <span className={`${file.status === 'uploaded' ? 'text-green-600' :
                                                file.status === 'failed' ? 'text-red-600' :
                                                    file.status === 'deleted' ? 'text-gray-600' : 'text-yellow-600'
                                                }`}>{file.status}</span></p>
                                        </div>

                                        {/* Actions */}
                                        <div className="mt-3 flex justify-end gap-2">
                                            {file.storageInfo?.publicUrl && (
                                                <TooltipProvider>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <button
                                                                onClick={(e) => handleCopyUrl(e, file)}
                                                                className="flex items-center justify-center w-8 h-8 text-green-600 bg-green-50 rounded-full hover:bg-green-100"
                                                            >
                                                                {copiedId === file._id ?
                                                                    <Check className="h-4 w-4" /> :
                                                                    <Copy className="h-4 w-4" />
                                                                }
                                                            </button>
                                                        </TooltipTrigger>
                                                        <TooltipContent side="top">
                                                            <p>{copiedId === file._id ? 'Copied!' : 'Copy URL'}</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            )}

                                            {(file.status === 'failed' || file.status === 'deleted') && (
                                                <TooltipProvider>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleRedownloadMedia(file._id, file.mediaId);
                                                                }}
                                                                className="flex items-center justify-center w-8 h-8 text-indigo-600 bg-indigo-50 rounded-full hover:bg-indigo-100"
                                                            >
                                                                <Download className="h-4 w-4" />
                                                            </button>
                                                        </TooltipTrigger>
                                                        <TooltipContent side="top">
                                                            <p>Redownload</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            )}

                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteMedia(file._id, file.status);
                                                            }}
                                                            className={`flex items-center justify-center w-8 h-8 ${file.status === 'deleted'
                                                                ? 'text-red-700 bg-red-100'
                                                                : 'text-red-600 bg-red-50'
                                                                } rounded-full hover:bg-red-100`}
                                                        >
                                                            <Trash2 className={`${file.status === 'deleted' ? 'h-4 w-4 animate-pulse' : 'h-4 w-4'
                                                                }`} />
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent side="top">
                                                        <p>{file.status === 'deleted' ? 'Permanently Delete' : 'Delete'}</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {pagination.pages > 1 && (
                    <div className="flex justify-center mt-8">
                        <nav className="flex items-center gap-1">
                            <button
                                onClick={() => handlePageChange(1)}
                                disabled={pagination.page === 1}
                                className={`px-3 py-1 rounded ${pagination.page === 1
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                            >
                                First
                            </button>
                            <button
                                onClick={() => handlePageChange(pagination.page - 1)}
                                disabled={pagination.page === 1}
                                className={`px-3 py-1 rounded ${pagination.page === 1
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                            >
                                Prev
                            </button>

                            <span className="px-3 py-1 text-sm text-gray-700">
                                Page {pagination.page} of {pagination.pages}
                            </span>

                            <button
                                onClick={() => handlePageChange(pagination.page + 1)}
                                disabled={pagination.page === pagination.pages}
                                className={`px-3 py-1 rounded ${pagination.page === pagination.pages
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                            >
                                Next
                            </button>
                            <button
                                onClick={() => handlePageChange(pagination.pages)}
                                disabled={pagination.page === pagination.pages}
                                className={`px-3 py-1 rounded ${pagination.page === pagination.pages
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                            >
                                Last
                            </button>
                        </nav>
                    </div>
                )}
            </div>
        </div>
    );
});

export default MediaList; 